import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Play, Power, PowerOff, Link2, Twitter, MessageCircle, Cloud, Code } from "lucide-react";

const PROVIDER_ICONS = {
  twitter_ifttt: Twitter,
  twitter_zapier: Twitter,
  discord: MessageCircle,
  slack: MessageCircle,
  bluesky_ifttt: Cloud,
  mastodon_ifttt: Cloud,
  custom: Code,
};

export default function AdminIntegrations() {
  const [catalog, setCatalog] = useState({ providers: [], categories: [] });
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);

  const load = async () => {
    try {
      const [{ data: c }, { data: r }] = await Promise.all([
        api.get("/admin/integrations/catalog"),
        api.get("/admin/integrations"),
      ]);
      setCatalog(c);
      setItems(r);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ id: null });
    setForm({
      name: "",
      provider: catalog.providers[0]?.key || "custom",
      url: "",
      category: "incident_broadcast",
      active: true,
      message_template: "",
    });
  };
  const openEdit = (r) => {
    setEditing({ id: r.id });
    setForm({
      name: r.name,
      provider: r.provider,
      url: r.url || "",
      category: r.category,
      active: r.active,
      message_template: r.message_template || "",
    });
  };
  const save = async () => {
    if (!form.name?.trim() || !form.url?.trim()) { toast.error("Nom et URL obligatoires"); return; }
    try {
      if (editing?.id) {
        await api.patch(`/admin/integrations/${editing.id}`, form);
        toast.success("Webhook mis à jour");
      } else {
        await api.post("/admin/integrations", form);
        toast.success("Webhook créé");
      }
      setEditing(null); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Supprimer "${r.name}" ?`)) return;
    await api.delete(`/admin/integrations/${r.id}`); toast.success("Supprimé"); load();
  };
  const toggle = async (r) => {
    await api.patch(`/admin/integrations/${r.id}`, { active: !r.active });
    toast.success(r.active ? "Désactivé" : "Activé"); load();
  };
  const test = async (r) => {
    try {
      const { data } = await api.post(`/admin/integrations/${r.id}/test`);
      if (data.ok) toast.success(`Test OK — HTTP ${data.status_code}`);
      else toast.error(`Test échoué — ${data.error || `HTTP ${data.status_code}`}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const selectedProvider = catalog.providers.find((p) => p.key === form?.provider);

  return (
    <div className="min-h-full">
      <PageHeader eyebrow="Intégrations" title="Webhooks & réseaux sociaux" sub="Configurez des URLs webhook pour Twitter/X, Discord, Slack, Bluesky, Mastodon — diffusion automatique des incidents majeurs." />
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-[#495057]">
            {items.length} intégration(s) · {items.filter((i) => i.active).length} active(s)
          </div>
          <Button onClick={openNew} data-testid="btn-new-integration" className="bg-[#002FA7] text-white">
            <Plus className="h-4 w-4 mr-2" /> Nouvelle intégration
          </Button>
        </div>

        <div className="grid gap-3" data-testid="integrations-list">
          {items.length === 0 ? (
            <div className="p-8 bg-white border border-[#DEE2E6] text-center text-sm text-[#868E96]">
              Aucune intégration configurée. Cliquez sur "Nouvelle intégration" pour connecter Twitter, Discord, Slack, Bluesky ou un webhook custom.
            </div>
          ) : items.map((r) => {
            const Icon = PROVIDER_ICONS[r.provider] || Link2;
            const providerLabel = catalog.providers.find((p) => p.key === r.provider)?.label || r.provider;
            return (
              <div key={r.id} className="bg-white border border-[#DEE2E6] p-4" data-testid={`integration-${r.id}`}>
                <div className="grid lg:grid-cols-12 gap-4 items-center">
                  <div className="lg:col-span-1">
                    <div className="h-10 w-10 border border-[#DEE2E6] flex items-center justify-center">
                      <Icon className="h-5 w-5 text-[#002FA7]" />
                    </div>
                  </div>
                  <div className="lg:col-span-5">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-display font-bold">{r.name}</div>
                      <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${r.active ? "bg-[#059669] text-white" : "bg-[#868E96] text-white"}`}>
                        {r.active ? "Active" : "Off"}
                      </span>
                      <span className="text-[9px] uppercase tracking-wider bg-[#F1F3F5] text-[#495057] px-1.5 py-0.5">
                        {providerLabel}
                      </span>
                    </div>
                    <div className="text-[10px] font-mono text-[#868E96] truncate">{r.url_masked}</div>
                    <div className="text-[10px] text-[#868E96] mt-1">
                      Catégorie : <b>{r.category}</b>
                      {r.run_count ? ` · ${r.run_count} envois` : ""}
                      {r.last_run_at ? ` · dernier ${new Date(r.last_run_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : ""}
                      {r.last_run_status ? ` (${r.last_run_ok ? "✓" : "✗"} ${r.last_run_status})` : ""}
                    </div>
                  </div>
                  <div className="lg:col-span-6 flex gap-1 flex-wrap justify-end">
                    <Button size="sm" onClick={() => test(r)} data-testid={`int-test-${r.id}`} variant="outline" className="h-8 text-xs"><Play className="h-3 w-3 mr-1" />Tester</Button>
                    <Button size="sm" onClick={() => toggle(r)} data-testid={`int-toggle-${r.id}`} variant="outline" className="h-8 text-xs">
                      {r.active ? <PowerOff className="h-3 w-3 mr-1" /> : <Power className="h-3 w-3 mr-1" />}
                      {r.active ? "Off" : "On"}
                    </Button>
                    <Button size="sm" onClick={() => openEdit(r)} data-testid={`int-edit-${r.id}`} variant="outline" className="h-8 text-xs">Éditer</Button>
                    <Button size="sm" onClick={() => del(r)} data-testid={`int-del-${r.id}`} variant="outline" className="h-8 text-xs border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {editing && form && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="int-dialog">
            <div className="bg-white max-w-2xl w-full my-8 p-6 border-2 border-[#0A0A0A]">
              <div className="font-display font-black text-2xl tracking-tighter mb-4">{editing.id ? "Éditer" : "Nouvelle"} intégration webhook</div>
              <div className="space-y-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Nom *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Alerte Discord équipe support" data-testid="int-name" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Provider</Label>
                  <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                    <SelectTrigger data-testid="int-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {catalog.providers.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {selectedProvider?.help && (
                    <div className="mt-1 text-[10px] text-[#495057] bg-[#F1F3F5] p-2 border-l-2 border-[#002FA7]">
                      💡 {selectedProvider.help}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">URL webhook *</Label>
                  <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="int-url" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Catégorie de diffusion</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="int-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {catalog.categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Template message (optionnel)</Label>
                  <div className="text-[10px] text-[#868E96] mb-1">Variables : {"{title}, {severity}, {service}, {message}"}</div>
                  <Textarea rows={3} value={form.message_template} onChange={(e) => setForm({ ...form, message_template: e.target.value })} placeholder="Ex: 🚨 PAG Incident {severity} · {title} — {service}" data-testid="int-tpl" />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="int-active" />
                  Activer immédiatement
                </label>
              </div>
              <div className="mt-6 flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setEditing(null); setForm(null); }} data-testid="int-cancel">Annuler</Button>
                <Button className="bg-[#002FA7] text-white" onClick={save} data-testid="int-save">Enregistrer</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
